// The development finance engine's markup, ported from the
// developmenteye.co.uk homepage: the quick-estimate card (the scheme's
// figures) and the flow (refine sections, results, adviser band). Same
// structure and CSS as bridging (bridging/markup.ts); the application dialog
// is sharedMarkup.ts.
import { escapeHtml } from '../format';
import type { DevFinanceForm, EngineConfig } from '../types';

function moneyField(id: string, label: string, span2 = false): string {
  return `
        <div class="field${span2 ? ' span2' : ''}">
          <label for="${id}">${label}</label>
          <div class="money-field"><span class="currency" aria-hidden="true">£</span><input type="text" inputmode="numeric" autocomplete="off" id="${id}" /></div>
        </div>`;
}

export function devEstimateHtml(flatCard: boolean): string {
  return `
<div class="estimate-wrap">
  <div class="estimate-card${flatCard ? ' flat' : ''}" role="region" aria-labelledby="estimate-title">
    <div class="card-head">
      <h2 id="estimate-title" class="estimate-title">Quick estimate</h2>
      <span class="hint">Development finance requirement</span>
    </div>

    <div class="estimate-grid">
      ${moneyField('input-site-value', 'Site value / purchase price')}
      ${moneyField('input-existing-debt', 'Existing debt on site (day 1)')}
      ${moneyField('input-build-cost', 'Total build cost (incl. fees &amp; contingency)')}
      ${moneyField('input-gdv', 'Gross development value (GDV)')}
      ${moneyField('input-cash-equity', 'Cash you can contribute', true)}
    </div>

    <dl class="facility-strip" aria-live="polite">
      <div><dt>Facility required</dt><dd id="facility-label">—</dd></div>
      <div><dt>Loan to cost</dt><dd id="ltc-label">—</dd></div>
      <div><dt>Loan to GDV</dt><dd id="ltgdv-label">—</dd></div>
    </dl>
    <p class="hint" style="margin-top:-6px">Facility required = site value + build cost − your cash.</p>

    <div class="rate-panel" aria-live="polite">
      <div class="rate-panel-top">
        <span class="rate-panel-label">Indicative rate from</span>
        <p id="rate-panel-headline" class="rate-panel-headline">Checking available products…</p>
        <div id="rate-panel-figure-wrap" class="rate-figure hidden"><span id="rate-panel-figure"></span><span class="pm">pm</span></div>
      </div>
      <div id="rate-panel-breakdown" class="rate-breakdown hidden">
        <div><div class="k">Facility</div><div class="v" id="rate-panel-facility"></div></div>
        <div><div class="k">Arrangement fee</div><div class="v" id="rate-panel-fee"></div></div>
        <div><div class="k">Total cost</div><div class="v" id="rate-panel-total"></div></div>
      </div>
    </div>

    <button type="button" class="btn btn-primary btn-block btn-lg" data-scroll="refine">Refine your scheme and see available products →</button>
    <p class="hint" style="font-size:12.5px">Indicative only, not an offer. Arrangement fees are usually deducted from the facility.</p>
  </div>
</div>`;
}

function section(id: string, n: number, title: string, shortTitle: string, lede: string, body: string, continueButton: string): string {
  return `
        <section class="q-section" id="sec-${id}" data-section="${id}" aria-labelledby="sec-${id}-title">
          <div class="q-summary">
            <div><span class="q-summary-title">${n} · ${title}</span><span class="q-summary-text" data-summary="${id}"></span></div>
            <button type="button" class="link-btn" data-change="${id}" aria-label="Change ${shortTitle.toLowerCase()}">Change</button>
          </div>
          <div class="q-body">
            <div class="q-head">
              <span class="q-badge" data-badge="${id}" aria-hidden="true">${n}</span>
              <div><h3 id="sec-${id}-title">${title}</h3><p>${lede}</p></div>
            </div>
            ${body}
            ${continueButton}
          </div>
        </section>`;
}

const pillsQuestion = (id: string, label: string, pillsId: string) => `
            <div class="q">
              <p class="q-label" id="q-${id}">${label}</p>
              <div class="pills" role="group" aria-labelledby="q-${id}" id="${pillsId}"></div>
            </div>`;

const numberField = (id: string, label: string, min: number) => `
              <div class="field">
                <label for="${id}">${label}</label>
                <input class="control" type="number" inputmode="numeric" min="${min}" id="${id}" />
              </div>`;

export function devFlowHtml(config: EngineConfig<DevFinanceForm>): string {
  const { brand, form } = config;
  const continueBtn = (id: string) => `<button type="button" class="btn btn-primary btn-block mobile-only" data-continue="${id}">Continue</button>`;
  const project = section('project', 1, 'The project', 'The project', "What you're building and where planning stands.", `
            ${pillsQuestion('project-type', 'What type of project is this?', 'input-project-type')}
            ${pillsQuestion('planning', 'Planning status', 'input-planning-status')}`, continueBtn('project'));
  const timing = section('timing', 2, 'Location &amp; timescales', 'Location and timescales', 'Where the site is, and how long the build and sale or refinance will take.', `
            <div class="q-row">
              <div class="field">
                <label for="input-region-group">Where is the site?</label>
                <select id="input-region-group" class="control">${form.regionGroups.map(([v, l]) => `<option value="${v}">${escapeHtml(l)}</option>`).join('')}</select>
              </div>
              <div class="field postcode-field" id="postcode-field">
                <label for="input-postcode">Postcode</label>
                <input id="input-postcode" class="control postcode-input" placeholder="e.g. SW1A 1AA" autocomplete="postal-code" />
              </div>
            </div>
            <div class="q-row">
              ${numberField('input-build-duration', 'Estimated build duration (months)', 1)}
              ${numberField('input-exit-duration', 'Estimated exit period after build (months)', 0)}
            </div>`, continueBtn('timing'));
  const experience = section('experience', 3, 'Experience &amp; credit', 'Experience and credit', 'Your track record, who is borrowing, and recent credit history.', `
            <div class="q-row">
              ${numberField('input-completed-units', 'Similar schemes/units completed in the last 3 years', 0)}
            </div>
            ${pillsQuestion('entity', 'Purchasing vehicle', 'input-borrower-entity')}
            ${pillsQuestion('adverse', 'Any adverse credit — CCJs, defaults or bankruptcy?', 'input-adverse')}`,
  '<button type="button" class="btn btn-primary btn-lg view-products-btn" data-continue="experience">View eligible products →</button>');

  const progressItem = (id: string, n: number, long: string, short: string, target: string) =>
    `<li data-progress="${id}"><button type="button" data-scroll="${target}"><span class="num">${n}</span><span><span class="long">${long}</span><span class="short-label">${short}</span></span></button></li>`;

  return `
<div class="flow-wrap">
<div class="case-flow">
  <div class="case-bar" role="region" aria-label="Your scheme">
    <div>
      <span class="case-bar-label">Your scheme</span>
      <span class="case-bar-values"><span data-case="facility">—</span> · <span class="hl"><span data-case="ltgdv">—</span> LTGDV</span></span>
    </div>
    <button type="button" class="btn btn-ghost-light btn-sm" data-scroll="estimate">Edit</button>
  </div>

  <section id="refine" class="refine" aria-labelledby="refine-title">
    <div class="wrap refine-head">
      <div>
        <p class="eyebrow">Step 2 of 2 · Refine</p>
        <h2 id="refine-title" class="display">Products priced <em class="accent">for your scheme.</em></h2>
        <p class="lede">Three short sections: the project, location and timescales, and your experience. Available products update as you answer.</p>
      </div>
      <button type="button" class="btn btn-outline" data-scroll="advisers">Not sure? Talk to an adviser →</button>
    </div>

    <div class="wrap refine-grid">
      <aside class="refine-aside">
        <div class="case-card on-dark">
          <div class="case-card-head"><h3>Your scheme</h3><button type="button" class="link-btn" data-scroll="estimate">Edit</button></div>
          <dl>
            <div><dt>Site value</dt><dd data-case="site">—</dd></div>
            <div><dt>Build cost</dt><dd data-case="build">—</dd></div>
            <div><dt>GDV</dt><dd data-case="gdv">—</dd></div>
            <div><dt>Facility required</dt><dd class="hl" data-case="facility">—</dd></div>
          </dl>
          <p class="case-note">Figures carried over from the quick estimate.</p>
        </div>
        <nav class="progress" aria-label="Refine progress">
          <ol>
            ${progressItem('project', 1, 'The project', 'Project', 'sec-project')}
            ${progressItem('timing', 2, 'Location &amp; timescales', 'Timing', 'sec-timing')}
            ${progressItem('experience', 3, 'Experience &amp; credit', 'Experience', 'sec-experience')}
            ${progressItem('compare', 4, 'Compare products', 'Compare', 'results')}
          </ol>
        </nav>
      </aside>

      <div class="refine-sections">${project}${timing}${experience}
      </div>
    </div>
  </section>

  <section id="results" class="results" aria-labelledby="results-headline">
    <div class="wrap">
      <div class="results-head">
        <div>
          <p class="eyebrow">4 · Compare products</p>
          <h2 id="results-headline" class="display" aria-live="polite" tabindex="-1">Complete the details above</h2>
          <p class="results-sub">Facility <strong id="results-loan-label"></strong><span id="results-criteria-line" class="criteria-line"></span></p>
        </div>
        <div id="results-sort-wrap" class="field sort hidden">
          <label for="input-results-sort">Sort by</label>
          <select id="input-results-sort" class="control">
            <option value="totalCost">Total cost (lowest first)</option>
            <option value="rate">Interest rate (lowest first)</option>
          </select>
        </div>
      </div>

      <div id="results-wrap" class="hidden">
        <div class="results-table dev-table" id="results-table">
          <div class="results-header" id="results-header-grid"></div>
          <ol class="results-rows" id="results-rows"></ol>
          <div class="results-more hidden" id="results-more-wrap">
            <button type="button" class="btn btn-soft" id="results-show-all-btn"></button>
          </div>
        </div>
        <p class="fine">Total cost includes interest and the arrangement fee over the build and exit period. Indicative only, not an offer.</p>
      </div>

      <div id="not-ready-wrap" class="empty">
        <p class="text-muted">Enter your site value, build cost and GDV in the quick estimate above and matching lenders will appear here automatically.</p>
      </div>

      <div id="no-results-wrap" class="empty hidden">
        <h3>An adviser can source terms for this scheme</h3>
        <p>We're not showing live products for this scheme, but our advisers can source terms directly from lenders.</p>
        <p class="text-muted">Send us your scheme and we'll come back to you.</p>
        <p style="margin-top:16px"><button type="button" class="btn btn-primary" data-talk-adviser>Send my scheme to an adviser →</button></p>
      </div>
    </div>
  </section>

  <section id="advisers" class="adviser-band on-dark" aria-labelledby="advisers-title">
    <div class="wrap adviser-inner">
      <div>
        <h2 id="advisers-title" class="display">Not sure which lender fits your scheme?</h2>
        <p>Our advisers review your scheme across the whole market and negotiate terms for you. No credit search is run at this stage.</p>
      </div>
      <div class="adviser-actions">
        <button type="button" class="btn btn-on-dark btn-lg" data-talk-adviser>Talk to an adviser →</button>
        <a href="tel:${escapeHtml(brand.phone.replace(/\s/g, ''))}" class="btn btn-ghost-light btn-lg">${escapeHtml(brand.phone)}</a>
      </div>
    </div>
  </section>
</div>
</div>`;
}
